import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { isEqual } from "lodash";
import { Check, PencilLine, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";

import {
  AIFeedbackCategory,
  type AIFeedbackResponse,
  AIOutputKind,
  apiGetAiOutputsByUuidFeedbackOptions,
  apiPostAiFeedback,
  type CreateAIFeedbackSchema,
  type CreateAISummarySchema,
  type PatientAISummary,
} from "../../../api";
import { errorText } from "../../../app/error-text";
import { Button as BaseButton } from "../../../components/ui-kit/base/button";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../../components/ui-kit/base/toggle-group";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { BaseCheckbox } from "../../../components/ui-kit/fields/base-checkbox";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Form } from "../../../components/ui-kit/form/form";
import { FormField } from "../../../components/ui-kit/form/form-field";
import {
  aiFeedbackCategories,
  aiFeedbackCategoryDescriptions,
} from "../../../constants/enum-to-text";
import { cn } from "../../../utils/shadcn-ui";

/**
 * The caller's like or dislike of one AI summary. Feedback rows are
 * append-only, so a tap writes a new row and the verdict shown is the one the
 * output's feedback list serves as the caller's newest.
 */
export function AiSummaryFeedback(props: {
  summary: PatientAISummary;
  userUuid: string;
}) {
  const { t } = useLingui();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [pending, setPending] = useState<number | null>(null);
  const [tapped, setTapped] = useState<number>();
  const { data, error, refetch } = useQuery(
    apiGetAiOutputsByUuidFeedbackOptions({
      path: { uuid: props.summary.uuid },
    }),
  );

  const verdict = data?.feedback.find((f) => f.user_uuid === props.userUuid);
  // The tap shows at once, the details included; a failed write falls back to
  // the saved verdict.
  const shownRating = pending ?? verdict?.rating;
  const dislike = verdict?.rating === DISLIKE ? verdict : undefined;
  const ref = useRef<HTMLDivElement>(null);
  const revealDetails = shownRating === DISLIKE && tapped === DISLIKE;

  // The rating row goes to the top, so its confirmation stays in view above
  // the details a dislike just opened.
  useEffect(() => {
    if (revealDetails) {
      ref.current?.scrollIntoView({ block: "start" });
    }
  }, [revealDetails]);

  async function save(body: CreateAIFeedbackSchema) {
    savingRef.current = true;
    setSaving(true);
    try {
      await apiPostAiFeedback({ body });
      await refetch({ throwOnError: true });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  async function rate(value: string) {
    // Radix reports a tap on the selected item as "": a verdict is replaced by
    // the other one, never cleared. A tap during a write is dropped, so rows
    // cannot land out of order and make an earlier tap the verdict.
    if (!value || savingRef.current) {
      return;
    }
    const rating = value === String(LIKE) ? LIKE : DISLIKE;
    setPending(rating);
    setTapped(rating);
    try {
      await save({
        kind: AIOutputKind.summary,
        output_uuid: props.summary.uuid,
        rating,
      });
    } finally {
      setPending(null);
    }
  }

  return (
    <div ref={ref} className="flex scroll-mt-3 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t pt-3 text-xs">
        <span
          role="status"
          className="flex min-w-0 items-center gap-1 text-muted-foreground"
        >
          <RatingStatus pending={pending != null} rating={verdict?.rating} />
        </span>
        {error ? (
          <ErrorMessage message={errorText(error)} />
        ) : (
          // Disabled until the caller's verdict is known, so a tap can't land
          // before the selection shows what is already saved.
          <ToggleGroup
            type="single"
            variant="choice"
            size="sm"
            spacing={1}
            aria-label={t`Was this summary useful?`}
            value={String(shownRating ?? "")}
            onValueChange={rate}
            disabled={!data}
          >
            <ToggleGroupItem value={String(LIKE)} className={RATING_ITEM_CLASS}>
              <ThumbsUp />
              <Trans>Helpful</Trans>
            </ToggleGroupItem>
            <ToggleGroupItem
              value={String(DISLIKE)}
              className={RATING_ITEM_CLASS}
            >
              <ThumbsDown />
              <Trans>Not helpful</Trans>
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>
      {shownRating === DISLIKE && (
        // A dislike still being saved has no row yet: its details open empty,
        // and disabled until the row lands.
        <DislikeDetails
          key={dislike?.uuid}
          summary={props.summary}
          verdict={dislike}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function RatingStatus(props: { pending: boolean; rating?: number | null }) {
  if (props.pending) {
    return <Trans>Saving…</Trans>;
  }
  if (props.rating === LIKE) {
    return (
      <Sent>
        <Trans>Marked as helpful</Trans>
      </Sent>
    );
  }
  if (props.rating === DISLIKE) {
    return (
      <Sent>
        <Trans>Marked as not helpful</Trans>
      </Sent>
    );
  }
  return <Trans>Was this summary useful?</Trans>;
}

/**
 * What was wrong with a disliked summary, and optionally the summary as it
 * should have read. Every field is optional: the dislike itself is already
 * saved, or, with no `verdict` yet, being saved while the form waits disabled.
 * The correction is the whole document, prefilled with the saved one, else the
 * model's, so fixing one fact is editing one sentence and the result diffs
 * against the output directly.
 */
function DislikeDetails(props: {
  summary: PatientAISummary;
  verdict?: AIFeedbackResponse;
  onSave: (body: CreateAIFeedbackSchema) => Promise<unknown>;
  saving: boolean;
}) {
  const { t } = useLingui();
  const promptId = useId();
  const draftKey = props.verdict?.uuid;
  const saved = savedDraft(props.verdict);
  const [draft, setDraftState] = useState(
    () => (draftKey && unsentDrafts.get(draftKey)) || saved,
  );
  // Set by the button, so only an editor the reviewer just opened takes focus,
  // not one the form restores.
  const [focusCorrection, setFocusCorrection] = useState(false);
  const expected = draft.expected;
  const unsent = !isEqual(
    dislikeBody(props.summary, draft),
    dislikeBody(props.summary, saved),
  );

  function setDraft(next: DislikeDraft) {
    setDraftState(next);
    if (draftKey) {
      unsentDrafts.set(draftKey, next);
    }
  }

  async function send() {
    await props.onSave(dislikeBody(props.summary, draft));
    if (draftKey) {
      unsentDrafts.delete(draftKey);
    }
  }

  return (
    <Form
      className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-3 text-xs"
      onSubmit={send}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <span id={promptId} className="font-semibold">
            <Trans>What went wrong?</Trans>
          </span>
          <HelperTooltip
            content={
              <Trans>
                Optionally, tell us why you marked this summary as not helpful.
              </Trans>
            }
          />
        </div>
        <div
          role="group"
          aria-labelledby={promptId}
          className="-mx-2 -mb-1.5 flex flex-col gap-0.5"
        >
          {CATEGORIES.map((category) => (
            <CategoryOption
              key={category}
              category={category}
              checked={draft.categories.includes(category)}
              onCheckedChange={(checked) =>
                setDraft({
                  ...draft,
                  categories: CATEGORIES.filter((c) =>
                    c === category ? checked : draft.categories.includes(c),
                  ),
                })
              }
              disabled={props.saving}
            />
          ))}
        </div>
      </div>
      <FormField label={<Trans>Comment</Trans>}>
        <TextInput
          multiline="autosize"
          value={draft.comment}
          onChange={(comment) => setDraft({ ...draft, comment })}
          placeholder={t`What did it miss or get wrong?`}
          maxLength={COMMENT_MAX_LENGTH}
          disabled={props.saving}
        />
      </FormField>
      {expected && (
        <div className="flex flex-col gap-2 border-t pt-3">
          <p>
            <span className="font-semibold">
              <Trans>Corrected summary</Trans>
            </span>
            {" · "}
            <span className="text-muted-foreground">
              <Trans>Left unchanged, nothing is sent</Trans>
            </span>
          </p>
          <FormField label={<Trans>Heading</Trans>}>
            <TextInput
              multiline="autosize"
              value={expected.heading}
              onChange={(heading) =>
                setDraft({ ...draft, expected: { ...expected, heading } })
              }
              maxLength={HEADING_MAX_LENGTH}
              autoFocus={focusCorrection}
              disabled={props.saving}
            />
          </FormField>
          <FormField label={<Trans>Reasoning</Trans>}>
            <TextInput
              multiline="autosize"
              value={expected.reasoning}
              onChange={(reasoning) =>
                setDraft({ ...draft, expected: { ...expected, reasoning } })
              }
              maxLength={REASONING_MAX_LENGTH}
              disabled={props.saving}
            />
          </FormField>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <BaseButton
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 bg-transparent"
          onClick={() => {
            setFocusCorrection(true);
            setDraft({
              ...draft,
              expected: expected
                ? null
                : (saved.expected ?? modelText(props.summary)),
            });
          }}
          disabled={props.saving}
        >
          {expected ? (
            <>
              <X />
              <Trans>Discard correction</Trans>
            </>
          ) : (
            <>
              <PencilLine />
              <Trans>Suggest a correction</Trans>
            </>
          )}
        </BaseButton>
        <div className="ml-auto flex items-center gap-2">
          <span
            role="status"
            className="flex items-center gap-1 text-muted-foreground"
          >
            <DetailsStatus unsent={unsent} verdict={props.verdict} />
          </span>
          <Button type="submit" size="sm" disabled={props.saving || !unsent}>
            <Trans>Send details</Trans>
          </Button>
        </div>
      </div>
    </Form>
  );
}

/** Patient safety is a severity flag, so it is checked in danger colours. */
function CategoryOption(props: {
  category: AIFeedbackCategory;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
}) {
  const labelId = useId();
  const descriptionId = useId();
  const severe = props.category === AIFeedbackCategory.patient_safety;

  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted has-disabled:pointer-events-none">
      <BaseCheckbox
        checked={props.checked}
        onCheckedChange={(checked) => props.onCheckedChange(checked === true)}
        disabled={props.disabled}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        className={cn(
          severe &&
            "data-[state=checked]:border-danger-border data-[state=checked]:bg-danger-solid data-[state=checked]:text-white dark:data-[state=checked]:bg-danger-solid",
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5 peer-disabled:opacity-50">
        <span
          id={labelId}
          className={cn(
            "font-medium",
            severe && props.checked && "text-danger-foreground",
          )}
        >
          {resolveEnumLabel(aiFeedbackCategories[props.category])}
        </span>
        <span id={descriptionId} className="text-muted-foreground">
          {resolveEnumLabel(aiFeedbackCategoryDescriptions[props.category])}
        </span>
      </span>
    </label>
  );
}

function DetailsStatus(props: {
  unsent: boolean;
  verdict?: AIFeedbackResponse;
}) {
  if (props.unsent) {
    return <Trans>Not sent yet</Trans>;
  }
  if (props.verdict && hasDetails(props.verdict)) {
    return (
      <Sent>
        <Trans>Details sent</Trans>
      </Sent>
    );
  }
  return null;
}

function Sent(props: { children: ReactNode }) {
  return (
    <>
      <Check aria-hidden className="size-3.5 text-success-foreground" />
      {props.children}
    </>
  );
}

interface DislikeDraft {
  categories: AIFeedbackCategory[];
  comment: string;
  /** The correction editor's text; null while the editor is closed. */
  expected: SummaryText | null;
}

interface SummaryText {
  heading: string;
  reasoning: string;
}

/**
 * The follow-up row repeats the dislike, since the newest row is the verdict.
 * db-api rejects an empty category list and a blank comment, so those are left
 * out of the body.
 */
function dislikeBody(
  summary: PatientAISummary,
  draft: DislikeDraft,
): CreateAIFeedbackSchema {
  return {
    kind: AIOutputKind.summary,
    output_uuid: summary.uuid,
    rating: DISLIKE,
    categories: draft.categories.length > 0 ? draft.categories : undefined,
    comment: draft.comment.trim() || undefined,
    expected: correction(summary, draft.expected),
  };
}

function savedDraft(verdict?: AIFeedbackResponse): DislikeDraft {
  const expected = verdict?.expected;
  return {
    categories: CATEGORIES.filter((c) => verdict?.categories?.includes(c)),
    comment: verdict?.comment ?? "",
    expected: expected
      ? {
          heading: typeof expected.heading === "string" ? expected.heading : "",
          reasoning:
            typeof expected.reasoning === "string" ? expected.reasoning : "",
        }
      : null,
  };
}

function hasDetails(verdict: AIFeedbackResponse): boolean {
  return (
    (verdict.categories?.length ?? 0) > 0 ||
    !!verdict.comment ||
    !!verdict.expected
  );
}

/**
 * The correction the editor's text amounts to, if any. db-api rejects one
 * with both fields blank, and the model's own text is not a correction.
 */
function correction(
  summary: PatientAISummary,
  text: SummaryText | null,
): CreateAISummarySchema | undefined {
  if (!text) {
    return undefined;
  }
  const heading = text.heading.trim();
  const reasoning = text.reasoning.trim();
  const model = modelText(summary);
  if (
    (!heading && !reasoning) ||
    (heading === model.heading && reasoning === model.reasoning)
  ) {
    return undefined;
  }
  return { heading: heading || null, reasoning: reasoning || null };
}

function modelText(summary: PatientAISummary): SummaryText {
  return {
    heading: summary.heading?.trim() ?? "",
    reasoning: summary.reasoning?.trim() ?? "",
  };
}

/**
 * Each verdict's details as last edited. The popover unmounts its content when
 * it closes, so a draft held only in state would go with a stray click
 * outside; kept here, it is back when the popover reopens on that verdict.
 */
const unsentDrafts = new Map<string, DislikeDraft>();

const LIKE = 5;
const DISLIKE = 1;
const CATEGORIES = Object.values(AIFeedbackCategory);
// db-api's limits on `comment` and on `expected`'s two fields
const COMMENT_MAX_LENGTH = 2000;
const HEADING_MAX_LENGTH = 200;
const REASONING_MAX_LENGTH = 2000;

const RATING_ITEM_CLASS =
  "text-xs data-[state=on]:[&_svg]:fill-current data-[state=on]:cursor-default";
